import React, { useState } from 'react';
import _ from 'shared/lib/gettext';
import type { Asset, Folder } from './types';
import AssetPreview from './AssetPreview';
import Button from '~/app/components/Button';
import FolderView from './components/Folder';
import AssetView from './components/Asset';
import CopyUrlButton from './components/CopyUrlButton';
import { convertedName, isConverting } from './assetUtils';
import NavigationBar from './components/NavigationBar';
import FlexRow from '@ff/ui/FlexRow';
import AddFolderButton from './components/AddFolderButton';
import Dropzone from './components/Dropzone';
import { styled } from '@ff/ui';
import Box from '@ff/ui/Box';

interface Props {
  error?: string,
  isFetching: boolean,
  path: string,
  navigateUp: () => void,
  navigateDown?: (string) => void,
  selectedAsset?: string;
  selectedAssetUrl?: string,
  setSelectedAsset?: (string) => void,
  assets?: Array<Asset>,
  folders?: Array<Folder>,
  inline: boolean,
  allowUpload?: boolean,
  uploading?: boolean,
  onDropHandler?: (acceptedFiles: File[], rejectedFiles: File[]) => void,
  addFolderHandler?: (name: string) => Promise<void>
}

export default function FileList(props: Props) {
  const {
    isFetching,
    error,
    path,
    navigateUp,
    navigateDown,
    selectedAsset,
    selectedAssetUrl,
    setSelectedAsset,
    assets = [],
    folders = [],
    inline,
    allowUpload,
    uploading,
    onDropHandler,
    addFolderHandler
  } = props;

  const [showAddFolderModal, setShowAddFolderModal] = useState(false);

  const NavBar = ({ openUploadModal }) => (
    <NavigationBar
      onClickBack={navigateUp}
      isDarkBackground={!inline}
      path={path}
    >
      {
        allowUpload && (
        <FlexRow columnGap="16px">
          <AddFolderButton
            showAddFolderModal={showAddFolderModal}
            setShowAddFolderModal={setShowAddFolderModal}
            onAddFolder={addFolderHandler}
          />
          <CopyUrlButton
            selectedAssetUrl={selectedAssetUrl}
          />
          <Button
            primary
            onClick={openUploadModal}
          >
            {_('UPLOAD_NEW')}
          </Button>
        </FlexRow>
        )
      }
    </NavigationBar>
  );

  const handleDrop = (acceptedFiles, rejectedFiles) => {
    // Check if any accepted file has a name conflict with existing assets
    const isConflictDetected = acceptedFiles.some(file => {
      const baseName = file.name.replace(/\.[^/.]+$/, ""); // Removes the extension
      const transformedName = `${baseName}-default.jpg`;
      return assets.some(asset => 
        asset.Name === file.name || asset.Name === transformedName
      );
    });
  
    // Confirm with the user if there's a name conflict, otherwise proceed
    if (!isConflictDetected || window.confirm(_('ASSET_SAME_FILE_NAME'))) {
      onDropHandler?.(acceptedFiles, rejectedFiles);
    }
  };

  return (
    <Dropzone
      inline={inline}
      allowUpload={allowUpload}
      onDropHandler={handleDrop}
      NavBar={NavBar}
    >
      {
        !isFetching &&
        <AssetBrowserContainer>
          <AssetBrowser>
            {
              error && !assets?.length && !folders.length ? (
                <ErrorMessage message={error} />
              ) : (
                <>
                  {
                    folders.map(({ Name: name }) => (
                      <FolderView
                        key={name}
                        name={name}
                        onClick={() => navigateDown(name)}
                      />
                    ))
                  }
                  {
                    assets.map((asset) => {
                      // A just-uploaded image's address stops working once its compressed copy
                      // replaces it: it can't be picked till then.
                      const converting = isConverting(asset, { uploading });
                      return (
                        <AssetView
                          key={asset.Key}
                          name={converting ? convertedName(asset.Name) : asset.Name}
                          converting={converting}
                          isSelected={selectedAsset === asset.Key}
                          onClick={converting ? undefined : () => setSelectedAsset(asset.Key)}
                        />
                      );
                    })
                  }
                </>
              )
            }
          </AssetBrowser>
          <AssetPreview
            asset={selectedAssetUrl}
            inAssetBrowser
          />
        </AssetBrowserContainer>
      }
    </Dropzone>
  );
}

const AssetBrowserContainer = styled(Box)({
  display: 'flex',
  minHeight: 'inherit',
  justifyContent: 'space-between'
});

const AssetBrowser = styled(Box)({
  cursor: 'pointer',
});

const ErrorMessage = ({ message }) => (
  <div>{`Unable to fetch assets: ${message || 'Unknown error occurred. Please try again'}`}</div>
);
